import type { BrowserWindow, ILogger, SpecMapComponentType } from '@wix/thunderbolt-symbols'
import {
	getChromiumOrBlinkBasedBrowserVersion,
	getIOSVersion,
	getSafariVersion,
	isChrome,
	isEdge,
	isFirefox,
	isIE,
	isOpera,
	isSafari,
} from '@wix/thunderbolt-commons'

// https://caniuse.com/?search=multiple+import+maps
const browserToMinimumSupportedVersion = {
	edge: 133,
	chrome: 133,
	safari: 18.4,
	opera: 118,
}
export const checkSupportsDynamicImportMaps = (window: NonNullable<BrowserWindow>, isEditor: boolean): boolean => {
	if (!isEditor) {
		// Load es module shims in editor only, but if it's iOS 16 or lower, we need to load the es module shims on live site as well
		const iosVersion = getIOSVersion(window)
		return iosVersion ? iosVersion > 16 : true
	}
	// ie and firefox do not support dynamic import maps (any version)
	if (isIE(window)) {
		return false
	}
	if (isFirefox(window)) {
		return false
	}

	if (isSafari(window)) {
		const version = getSafariVersion(window)
		return version !== -1 && version >= browserToMinimumSupportedVersion.safari
	}

	// these are chromium/blink-based browsers, parsing the version from the user agent is similar
	if (isEdge(window)) {
		const version = getChromiumOrBlinkBasedBrowserVersion(window, 'edge')
		return version >= browserToMinimumSupportedVersion.edge
	}
	if (isChrome(window)) {
		const version = getChromiumOrBlinkBasedBrowserVersion(window, 'chrome')
		return version >= browserToMinimumSupportedVersion.chrome
	}
	if (isOpera(window)) {
		const version = getChromiumOrBlinkBasedBrowserVersion(window, 'opera')
		return version >= browserToMinimumSupportedVersion.opera
	}
	// default - return false so es module shims will be loaded.
	return false
}

export const extractEditorReactComponentImports = (
	editorReactComponents: Array<SpecMapComponentType<'EDITOR_REACT_COMPONENT'>>
): Record<string, string> => {
	return editorReactComponents.reduce<Record<string, string>>((acc, component) => {
		const resources = component.data.resources
		const moduleSpecifier = resources?.client?.moduleSpecifier
		if (!moduleSpecifier) {
			return acc
		}
		const componentUrl = resources.client?.componentUrl
		const editorComponentUrl = resources.editor?.componentUrl
		const sdkUrl = resources.sdk?.url
		acc[moduleSpecifier] = editorComponentUrl || componentUrl || ''
		sdkUrl && (acc[`${moduleSpecifier}/sdk`] = sdkUrl)
		return acc
	}, {})
}

export const ES_MODULE_SHIMS_URL =
	'https://static.parastorage.com/services/thunderbolt-shims-static/1.0.0/es-module-shims.js'

export const initEsModuleShims = (browserWindow: BrowserWindow, logger: ILogger): Promise<void> => {
	return new Promise<void>((resolve, reject) => {
		;(browserWindow as any).esmsInitOptions = {
			shimMode: true,
		}
		const esModuleShimsScript = browserWindow!.document.createElement('script')
		esModuleShimsScript.src = ES_MODULE_SHIMS_URL
		esModuleShimsScript.async = true

		logger.interactionStarted('esm-shim-load')

		esModuleShimsScript.onload = () => {
			logger.interactionEnded('esm-shim-load')
			resolve()
		}

		esModuleShimsScript.onerror = () => {
			const error = new Error(`ESM shim script failed to load from ${ES_MODULE_SHIMS_URL}`)
			logger.captureError(error, {
				tags: { feature: 'builder-module-loader' },
				extra: { url: ES_MODULE_SHIMS_URL },
			})
			reject(error)
		}

		browserWindow!.document.head.appendChild(esModuleShimsScript)
	})
}
